import React, { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";

const LABELS = { 1: "Poor", 2: "Fair", 3: "Okay", 4: "Good", 5: "Excellent" };

export const RateCallDialog = ({ prompt, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!prompt) return null;

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await api.post(`/calls/${prompt.call_id}/rate`, { rating, comment: comment || null });
      toast.success("Thanks for your feedback!");
    } catch {
      /* silent — rating is best-effort */
    }
    onClose();
  };

  const shown = hover || rating;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]" data-testid="rate-call-dialog">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-200">
        <div className="text-3xl mb-1">{prompt.flag}</div>
        <h3 className="font-heading text-lg font-bold">How was your call?</h3>
        <p className="text-sm text-zinc-500 mb-4">
          {prompt.country} • {prompt.talkTime}
        </p>

        <div className="flex justify-center gap-2 mb-1" data-testid="rate-call-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              onClick={() => setRating(s)}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              className="p-1 transition-transform active:scale-90"
              data-testid={`rate-star-${s}`}
            >
              <Star
                className={`h-9 w-9 transition-colors ${s <= shown ? "text-amber-400 fill-amber-400" : "text-zinc-300"}`}
              />
            </button>
          ))}
        </div>
        <div className="h-5 text-sm font-semibold text-amber-600 mb-3" data-testid="rate-label">
          {shown ? LABELS[shown] : ""}
        </div>

        {rating > 0 && rating <= 3 && (
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="What went wrong? (optional)"
            className="w-full border border-zinc-200 rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="rate-comment-input"
          />
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-zinc-500 font-semibold text-sm hover:bg-zinc-50 transition-colors"
            data-testid="rate-skip-button"
          >
            Skip
          </button>
          <button
            onClick={submit}
            disabled={!rating || submitting}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white font-semibold text-sm disabled:opacity-40 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            data-testid="rate-submit-button"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
